import Loading from '@common/components/Loading';
import { authenticationRouting } from '@common/hooks/authenticationRoute';
import { currentRecordSelector } from '@common/redux/selectors/database';
import { dimensionSelector } from '@common/redux/selectors/page';
import { setCurrentRecordIds } from '@common/redux/slice/database';
import history from '@common/routes/history';
import { IAppInfo } from '@common/types/element';
import { IapWrapper } from '@common/utils/IapWrapper';
import { extractDependenciesFromObject } from '@common/utils/binding';
import { isEmpty, uniq } from 'lodash';
import React, { Fragment, useEffect, useMemo } from 'react';
import { Platform, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import BaseScreen from './BaseScreen';
import ForgotPasswordWrap from './ForgotPassword';
import HelmetCustom from './Helmet';
import ModalScreen from './ModalScreen';
import ResetPasswordWrap from './ResetPassword';

let SplashScreen: any;

(async () => {
  if (Platform.OS !== 'web') {
    SplashScreen = await (await import('react-native-splash-screen')).default;
  }
})();

type Props = {
  appInfo: IAppInfo;
};

function RenderScreen({ appInfo }: Props) {
  const dimension = useSelector(dimensionSelector);

  const { screen, animation, modalScreen, modalTransition, loading } =
    authenticationRouting({ appInfo });

  const currentRecordIds = useSelector(currentRecordSelector);

  const dispatch = useDispatch();

  const _routeParamsStr: string = useMemo(() => {
    let routeParams: string[] = [];
    if (screen?.metadata && Array.isArray(screen?.metadata)) {
      screen?.metadata?.forEach((obj) => {
        const { routeParams: _1 } = extractDependenciesFromObject(obj ?? {});
        routeParams = [...routeParams, ..._1];
      });
      return uniq(routeParams).join(',');
    }
    return '';
  }, [screen?.metadata]);

  // use string to avoid rerender when array is empty
  const routeParams: string[] = useMemo(() => {
    return _routeParamsStr.split(',');
  }, [_routeParamsStr]);

  if (Platform.OS !== 'web') {
    SplashScreen.hide();
  }

  // add currentRecordIds to URI when currentRecordIds is changed
  // use search params to store currentRecordIds
  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    const recordIdChanged: Record<string, string> = {};
    const searchParams = new URLSearchParams(history.location.search);
    // delete routeParams from searchParams if not in currentRecordIds
    Object.keys(currentRecordIds).forEach((key: string) => {
      if (!routeParams.includes(key) && searchParams.has(key)) {
        searchParams.delete(key);
      }
    });
    // get different between currentRecordIds and searchParams, the searchParams is higher priority
    routeParams.forEach((key: string) => {
      const searchParamValue = searchParams.get(key);
      if (
        currentRecordIds[key] &&
        searchParamValue &&
        currentRecordIds[key] === searchParamValue
      ) {
        return;
      }
      if (currentRecordIds[key]) {
        searchParams.set(key, currentRecordIds[key]);
        return;
      }
      if (searchParamValue) {
        recordIdChanged[key] = searchParamValue;
        return;
      }
    });
    const isSearchParamsChanged =
      searchParams.toString() !==
      new URLSearchParams(history.location.search).toString();
    if (isSearchParamsChanged) {
      // update searchParams
      history.replace({
        search: searchParams.toString(),
      });
    }
    // if currentRecordIds and searchParams is different, update them
    if (!isEmpty(recordIdChanged)) {
      // set currentRecordIds to redux
      dispatch(
        setCurrentRecordIds({ ...currentRecordIds, ...recordIdChanged })
      );
    }
  }, [currentRecordIds, routeParams, history.location.search]);

  if (loading) {
    return <Loading />;
  }

  if (!screen) {
    return (
      <Fragment>
        <View
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            width: dimension.width,
            height: dimension.height,
            backgroundColor: 'white',
          }}
        >
          {/* <Text>Not found screen on App</Text> */}
        </View>
      </Fragment>
    );
  }

  return (
    <IapWrapper screen={screen}>
      <Fragment>
        <HelmetCustom screen={screen} />

        <BaseScreen screen={screen} animation={animation} />

        <ModalScreen
          modalScreen={modalScreen}
          modalTransition={modalTransition}
        />

        <ResetPasswordWrap />

        <ForgotPasswordWrap />
      </Fragment>
    </IapWrapper>
  );
}

export default RenderScreen;
